<script setup>
import { router, useForm } from '@inertiajs/vue3'
import {
    onMounted,
    onUnmounted,
    ref,
    computed,
} from 'vue'
import { initFlowbite } from 'flowbite'

let unsubscribeRouter = null

const initializeModal = () => {
    initFlowbite()
}

onMounted(() => {
    initializeModal()

    // Listen for Inertia navigation events to re-initialize Flowbite
    unsubscribeRouter = router.on('navigate', () => {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
            initializeModal()
        }, 100)
    })
})

onUnmounted(() => {
    // Clean up router listener
    if (unsubscribeRouter) {
        unsubscribeRouter()
    }
})

const baseUrl = window.location.href // Replace with your base URL

// Compute the full video URL
const videoUrl = computed(() => {
    return baseUrl
})
const generateVideoUrl = ref(window.location.href)

const showCopiedTip = ref(false)

const copyToClipboard = () => {
    generateVideoUrl.value = window.location.href
    const el = document.createElement('textarea')
    el.value = generateVideoUrl.value
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)

    // Show the "Copied" tip
    showCopiedTip.value = true

    // Hide the "Copied" tip after a delay (e.g., 3 seconds)
    setTimeout(() => {
        showCopiedTip.value = false
    }, 2000)
}

const shareOnTwitter = () => {
    // Construct the Twitter share URL with your videoUrl
    const tweetUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        videoUrl.value,
    )}`

    // Open the Twitter sharing dialog in a new window or tab
    window.open(tweetUrl, '_blank')
}
const shareOnReddit = () => {
    // Construct the Reddit share URL with your videoUrl
    const redditUrl = `https://www.reddit.com/submit?url=${encodeURIComponent(videoUrl.value)}`

    // Open the Reddit sharing dialog in a new window or tab
    window.open(redditUrl, '_blank')
}
const shareOnWhatsApp = () => {
    // Construct the WhatsApp share URL with your videoUrl
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
        videoUrl.value,
    )}`

    // Open the WhatsApp sharing dialog in a new window or tab
    window.open(whatsappUrl, '_blank')
}
const shareOnTelegram = () => {
    // Construct the Telegram share URL with your videoUrl
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(videoUrl.value)}`

    // Open the Telegram sharing dialog in a new window or tab
    window.open(telegramUrl, '_blank')
}

const shareViaSMS = () => {
    // Construct the SMS message with your videoUrl
    const smsMessage = `Check out this Extra Naughty ${type}: ${videoUrl.value}`

    // Open the user's default SMS application with the message pre-filled
    window.location.href = `sms:&body=${encodeURIComponent(smsMessage)}`
}
const formReport = useForm({
    reason: '',
    message: '',
})
const { report, urlID, type } = defineProps(['report', 'urlID', 'type'])
const showReportModal = ref(false)

// Swipe-to-close (mobile pull handle)
const panelRef = ref(null)
const touchStartY = ref(0)
const dragOffset = ref(0)
const isDragging = ref(false)

const closeModal = () => {
    const flowbiteModal = window.FlowbiteInstances?.getInstance('Modal', 'ShareLinkModal')
    if (flowbiteModal && flowbiteModal.isVisible()) {
        // Opened via Flowbite (data-modal-toggle) — let it clean up its own backdrop
        flowbiteModal.hide()
    } else {
        // Opened manually (e.g. classList.remove('hidden')) — just hide the element
        document.getElementById('ShareLinkModal')?.classList.add('hidden')
    }
    // Reset any drag transform on the panel
    if (panelRef.value) {
        panelRef.value.style.transform = ''
        panelRef.value.style.transition = ''
    }
}

const handleTouchStart = (e) => {
    touchStartY.value = e.touches[0].clientY
    dragOffset.value = 0
    isDragging.value = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return
    const deltaY = e.touches[0].clientY - touchStartY.value
    if (deltaY > 0) {
        e.preventDefault()
        dragOffset.value = deltaY
        if (panelRef.value) {
            panelRef.value.style.transform = `translateY(${deltaY}px)`
            panelRef.value.style.transition = 'none'
        }
    }
}

const handleTouchEnd = () => {
    if (!isDragging.value) return
    isDragging.value = false
    if (panelRef.value) {
        panelRef.value.style.transition = 'transform 0.3s ease-out'
        panelRef.value.style.transform = ''
    }
    if (dragOffset.value > 100) {
        closeModal()
    }
    dragOffset.value = 0
}
</script>

<template>
    <Teleport to="body">
        <!-- Visibility is controlled by toggling the `hidden` class via JS. -->
        <div
            id="ShareLinkModal"
            tabindex="-1"
            class="fixed inset-0 z-50 hidden bg-black/20 backdrop-blur-md">

            <!-- Inner positioning wrapper — fully under our control -->
            <div class="flex h-full items-end justify-center md:items-center md:p-6">

                <!-- Panel -->
                <div
                    ref="panelRef"
                    class="w-full max-w-md rounded-t-3xl md:rounded-3xl overflow-hidden bg-zinc-950/90 backdrop-blur-2xl border border-white/10 shadow-2xl">

                    <!-- Pull handle (mobile only) -->
                    <div
                        class="md:hidden flex justify-center pt-2.5 pb-2 cursor-grab active:cursor-grabbing"
                        @touchstart="handleTouchStart"
                        @touchmove.prevent="handleTouchMove"
                        @touchend="handleTouchEnd">
                        <div class="h-1 w-10 rounded-full bg-white/15" />
                    </div>

                    <!-- Header -->
                    <div class="flex items-center justify-between px-5 pt-3 pb-3">
                        <p class="text-white text-[15px] font-semibold tracking-tight">Share</p>
                        <button
                            type="button"
                            @click="closeModal"
                            class="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition"
                            aria-label="Close">
                            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    <div class="h-px bg-white/10" />

                    <!-- Body -->
                    <div class="p-5 space-y-5">

                        <!-- Social icons -->
                        <div class="flex justify-center gap-4">
                            <button
                                title="Share on SMS"
                                @click="shareViaSMS"
                                class="flex h-12 w-12 items-center justify-center rounded-full bg-smsgreen hover:opacity-85 transition-opacity">
                                <font-awesome-icon class="text-2xl text-white" :icon="['fas', 'comment-sms']" />
                            </button>
                            <button
                                @click="shareOnTwitter"
                                title="Share on X / Twitter"
                                class="flex h-12 w-12 items-center justify-center rounded-full bg-xblack hover:opacity-85 transition-opacity">
                                <font-awesome-icon icon="fa-brands fa-x-twitter" class="text-2xl text-white" />
                            </button>
                            <button
                                @click="shareOnReddit"
                                title="Share on Reddit"
                                class="flex h-12 w-12 items-center justify-center rounded-full bg-redditred hover:opacity-85 transition-opacity">
                                <font-awesome-icon class="text-2xl text-white" :icon="['fab', 'reddit']" />
                            </button>
                            <button
                                @click="shareOnWhatsApp"
                                title="Share on WhatsApp"
                                class="flex h-12 w-12 items-center justify-center rounded-full bg-whatsappgreen hover:opacity-85 transition-opacity">
                                <font-awesome-icon class="text-2xl text-white" :icon="['fab', 'whatsapp']" />
                            </button>
                            <button
                                @click="shareOnTelegram"
                                title="Share on Telegram"
                                class="flex h-12 w-12 items-center justify-center rounded-full bg-telegramblue hover:opacity-85 transition-opacity">
                                <font-awesome-icon class="text-2xl text-white" :icon="['fab', 'telegram']" />
                            </button>
                        </div>

                        <!-- URL copy — flex row, no absolute positioning -->
                        <div class="relative">
                            <div class="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.06] pl-4 pr-1.5 py-1.5">
                                <input
                                    type="text"
                                    :value="videoUrl"
                                    class="flex-1 min-w-0 bg-transparent text-sm text-white/80 focus:outline-none"
                                    readonly />
                                <button
                                    @click="copyToClipboard"
                                    title="Copy to Clipboard"
                                    type="button"
                                    class="shrink-0 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 px-3 py-2 transition">
                                    <font-awesome-icon class="text-base text-white/60" :icon="['fas', 'copy']" />
                                </button>
                            </div>
                            <div
                                v-show="showCopiedTip"
                                class="absolute -top-7 right-0 rounded-full bg-green-500/90 px-2.5 py-1 text-xs font-medium text-white">
                                Copied!
                            </div>
                        </div>
                    </div>

                    <!-- Bottom safe-area spacer (mobile) -->
                    <div class="pb-[calc(env(safe-area-inset-bottom)+0.75rem)] md:pb-0" />
                </div>
            </div>
        </div>
    </Teleport>
</template>
